import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import Translate from '../../components/Translate';
import PageHeader from '../../components/PageHeader';
import { db } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { getLangText } from '../../utils/langHelper';

const Administration = () => {
  const [activeImg, setActiveImg] = useState(null);
  const [pageData, setPageData] = useState({ services: [], galleryImages: [] });
  const { i18n } = useTranslation();
  const lang = i18n.language;

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'divisions', 'administration'), (docSnap) => {
      if (docSnap.exists()) {
        const dbData = docSnap.data();
        setPageData({
          ...dbData,
          services: dbData.services || [],
          galleryImages: dbData.galleryImages || []
        });
      } else {
        setPageData({ services: [], galleryImages: [] });
      }
    });
    return unsub;
  }, []);

  const heroTitle = getLangText(pageData, 'heroTitle', lang);
  const introImage = pageData.introImage;
  const introLabel = getLangText(pageData, 'introLabel', lang);
  const introDesc = getLangText(pageData, 'introDesc', lang);
  const services = pageData.services || [];
  const galleryImages = pageData.galleryImages || [];

  return (
    <div className="min-h-screen bg-white font-body selection:bg-emerald selection:text-white">
      {/* ── 1. HERO SECTION (Identical to About Page Header) ── */}
      <PageHeader 
        title={<Translate>{heroTitle}</Translate>} 
      />

      {/* ── 2. OVERVIEW SECTION ── */}
      <section id="intro" className="py-20 lg:py-24 bg-ivory">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-6 space-y-6 pt-2">
              <div>
                <span className="section-label block mb-3 text-xs md:text-sm font-heading font-extrabold uppercase tracking-widest text-emerald"><Translate>{introLabel}</Translate></span>
                <div className="gold-divider mt-4"></div>
              </div>

              <div className="space-y-6 text-slate text-base md:text-lg leading-relaxed font-medium">
                <p>{introDesc}</p>
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-emerald/10 to-gold/15 rounded-3xl -z-10 transform rotate-2"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-premium-xl border border-gray-150 group">
                <img 
                  src={introImage} 
                  alt="Corporate Administration" 
                  className="w-full h-[450px] object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/40 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. SERVICES SECTION ── */}
      <section className="py-20 bg-mist">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="section-label block mb-3"><Translate>SUPPORT SYSTEMS</Translate></span>
            <h2 className="section-title"><Translate>Corporate Admin Services</Translate></h2>
            <div className="gold-divider mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((srv, idx) => {
              const srvTitle = getLangText(srv, 'name', lang) || getLangText(srv, 'title', lang);
              const srvDesc = getLangText(srv, 'desc', lang);
              return (
                <div 
                  key={idx}
                  className="p-8 bg-white rounded-3xl border border-gray-100/80 group hover:border-emerald/30 transition-all duration-300 shadow-premium hover:shadow-premium-lg"
                >
                  <h3 className="text-lg font-heading font-bold text-charcoal mb-3 group-hover:text-emerald transition-colors duration-300">
                    <Translate>{srvTitle}</Translate>
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed font-body">
                    <Translate>{srvDesc}</Translate>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 4. GALLERY ── */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-16">
            <span className="section-label block mb-3"><Translate>VISUAL RECORD</Translate></span>
            <h2 className="section-title"><Translate>Administration Gallery</Translate></h2>
            <div className="gold-divider mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((img, idx) => (
              <div 
                key={idx} 
                className="relative overflow-hidden rounded-2xl cursor-pointer group shadow-premium hover:shadow-premium-lg"
                onClick={() => setActiveImg(img)}
              >
                <img 
                  src={img} 
                  alt={`Administration Gallery ${idx + 1}`} 
                  className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-charcoal/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="bg-white/95 backdrop-blur-sm text-charcoal font-heading font-bold px-4 py-2 rounded-xl text-xs shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <Translate>Expand Image</Translate>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {activeImg && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-charcoal/90 backdrop-blur-md flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setActiveImg(null)}
          >
            <button className="absolute top-6 right-6 p-3 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors">
              <X size={24} />
            </button>
            <motion.img 
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              src={activeImg} 
              alt="Expanded Administration Gallery" 
              className="max-w-full max-h-[90vh] rounded-xl object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Administration;
